import { useRef, useMemo, useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Thumbs } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import shuffle from 'lodash/shuffle'
//
import { selectNavigationContent } from 'redux/selector/app'
//
import {
  ENABLED,
  NAV_ALIGN_CENTER,
  NAV_ALIGN_LEFT,
  NAV_ALIGN_RIGHT,
  NAV_TYPE_NORMAL,
  SECTION_HF_TYPE_FOOTER,
  SECTION_HF_TYPE_HEADER,
  TRUE,
} from 'constant/common'

import useEvents from './hooks/useEvents'
import { Wrapper, TabsContentWrapper, TabsTitleWrapper } from './Styled'
import Header from 'component/Header'
import { HeaderWrap } from 'commonStyledComponents'
import { CmsTab } from './component/CmsTab'
import { useAppLocation } from 'app-layered-layout/useAppLocation'
import FloatPromotion from 'component/FloatPromotion'
import { useResizeObserver } from 'hooks/useResizeObsever'
import { report } from 'hooks/useAnalytics'

const TITLE_ALIGN_MAP: Record<string, string> = {
  [NAV_ALIGN_LEFT]: 'left',
  [NAV_ALIGN_CENTER]: 'center',
  [NAV_ALIGN_RIGHT]: 'right',
}

const HIDE_PROMO_BOX_AFTER_TAB_CHANGE_FOR_MS = 2000

export default function CmsView() {
  const location = useAppLocation()
  const [thumbsSwiper] = useState<any>(null)

  /** 所有Nav頁面資料 */
  /** @type {import('api').SchemaCmsPublishResults} */
  const navsInfo = useSelector(selectNavigationContent)

  const navId = location.search ? /nav_id=(\w+)/.exec(location.search)?.[1] ?? null : null
  let currentNavIndex = navId
    ? navsInfo.findIndex((i: any) => i.id === navId)
    : navsInfo.findIndex((i: any) => i.type === NAV_TYPE_NORMAL)

  if (currentNavIndex === -1) {
    currentNavIndex = 0
  }

  /** 當前 Nav 內的所有頻道 */
  const tabsInfo = useMemo(() => {
    return navsInfo?.[currentNavIndex]?.tabs || []
  }, [navsInfo, currentNavIndex])

  const hideHeader =
    navsInfo?.[currentNavIndex]?.hide_head != null ? navsInfo?.[currentNavIndex]?.hide_head === ENABLED : false

  const { activeTabIndex, setActiveTabIndex } = useEvents()
  const tabContentRef = useRef<any>()
  // const swiperSlide = useSwiperSlide();

  const jumpTo = useRef<number | null>(null)

  const handleTabSwitch = useCallback(
    (activeIndex: number) => {
      // N1-5270：僅在點擊頻道 tab 時上報（左右滑動走 onSlideChange，不會進到這裡）
      report({ category: 'CMS_Tab', action: 'Click_Channel', name: tabsInfo[activeIndex]?.name })

      jumpTo.current = activeIndex
      tabContentRef?.current?.swiper.slideTo(activeIndex)

      const activeElement = document.getElementsByClassName('tab-title active')?.[0]
      activeElement?.scrollIntoView({ behavior: 'smooth', inline: 'center' })
    },
    [tabsInfo]
  )

  const onSlideChange = useCallback(
    (swiper: any) => {
      if (activeTabIndex !== swiper.activeIndex) {
        if (jumpTo.current != null && swiper.activeIndex === jumpTo.current) {
          jumpTo.current = null
        }
        setActiveTabIndex(swiper.activeIndex)
      }
    },
    [activeTabIndex, setActiveTabIndex]
  )

  const titleAlign = navsInfo?.[currentNavIndex]?.align ?? NAV_ALIGN_LEFT

  useEffect(() => {
    setActiveTabIndex(0)
  }, [currentNavIndex, setActiveTabIndex])

  /** @typedef {import("api").SchemaCmsTabDetail} SchemaCmsTabDetail */
  /**
   * tab 隨機排序的 cache
   * @type {React.MutableRefObject<Map<SchemaCmsTabDetail, SchemaCmsTabDetail>>  }>}
   */
  const shuffledAreaData = useRef<Map<any, any>>(new Map())

  /**
   * @param {import('api').SchemaCmsSectionShow} section
   * @returns {'top' | 'normal' | 'bottom'}
   */
  const getShuffleType = (section: any): 'top' | 'normal' | 'bottom' => {
    if (!section.hf_type) {
      if (section.top_seqno > 0) {
        return 'top'
      } else {
        return 'normal'
      }
    }

    if (section.hf_type === SECTION_HF_TYPE_HEADER) {
      return 'top'
    }

    if (section.hf_type === SECTION_HF_TYPE_FOOTER) {
      return 'bottom'
    }

    return 'normal'
  }

  /**
   * 取得對應該 tab 的隨機排序，沒有的話就生成一個
   * @param {SchemaCmsTabDetail} original
   */
  const getShuffledArea = (original: any) => {
    if (shuffledAreaData.current.get(original)) {
      return shuffledAreaData.current.get(original)
    }

    // seqno > 0 按seqno由大到小排序放置於前排
    const topList = original.sections
      .filter((e: any) => getShuffleType(e) === 'top')
      .sort((a: any, b: any) => b.top_seqno - a.top_seqno)

    const doShuffleList = original.sections.filter((e: any) => getShuffleType(e) === 'normal')
    const shuffledSection = shuffle(doShuffleList || [])

    const bottomList = original.sections
      .filter((e: any) => getShuffleType(e) === 'bottom')
      .sort((a: any, b: any) => b.top_seqno - a.top_seqno)

    const shuffled = {
      ...original,
      sections: [...topList, ...shuffledSection, ...bottomList],
    }

    shuffledAreaData.current.set(original, shuffled)
    return shuffled
  }

  /** 在變更 nav 時清空記憶下來的隨機排序 */
  useEffect(() => {
    shuffledAreaData.current = new Map()
  }, [currentNavIndex])

  const [tabRefreshKeys, setTabRefreshKeys] = useState<Record<string, string>>({})

  const getKeyOfTab = useCallback((nav: number, tab: number) => `${nav}_${tab}`, [])

  const getKeySuffix = useCallback(
    (nav: number, tab: number) => {
      const existingKey = tabRefreshKeys[getKeyOfTab(nav, tab)]
      return existingKey ? `_${existingKey}` : ''
    },
    [getKeyOfTab, tabRefreshKeys]
  )

  const onRefresh = useCallback(
    async (tabIndex: number) => {
      const tabToRefresh = tabsInfo[tabIndex]
      shuffledAreaData.current.delete(tabToRefresh)
      setTabRefreshKeys((oldKeys) => ({
        ...oldKeys,
        [getKeyOfTab(currentNavIndex, tabIndex)]: Math.random().toString(16).slice(2),
      }))
    },
    [currentNavIndex, getKeyOfTab, tabsInfo]
  )

  const [shortcutPositions, setShortcutPositions] = useState<Record<number, DOMRect | undefined>>({})

  const onPositionChange = useCallback((index: number, box: DOMRect | undefined) => {
    setShortcutPositions((old) => ({ ...old, [index]: box }))
  }, [])

  const [floatPromoBox, setFloatPromoBox] = useState<DOMRect | null>(null)
  const floatPromoRef = useRef<HTMLElement | null>(null)
  const onFloatPromoRefed = useCallback((el: HTMLElement | null) => {
    floatPromoRef.current = el
    setFloatPromoBox(el?.getBoundingClientRect() ?? null)
  }, [])

  const resizeRef = useResizeObserver({
    onResize() {
      setFloatPromoBox(floatPromoRef.current?.getBoundingClientRect() ?? null)
    },
  })

  const overlayWithPromoBox = (index: number | null) => {
    if (index == null) {
      return false
    }
    let overlay = false
    const nextActiveBox = shortcutPositions[index]
    if (nextActiveBox && floatPromoBox) {
      if (floatPromoBox.top < nextActiveBox.bottom) {
        overlay = true
      }
    }
    return overlay
  }

  const [hideUntil, setHideUntil] = useState<number | null>(null)
  const [delayedActiveTabIndex, setDelayedActiveTabIndex] = useState<number | null>(null)

  useEffect(() => {
    const now = Date.now()
    if (hideUntil != null) {
      if (hideUntil > now) {
        const id = setTimeout(() => {
          setHideUntil(null)
        }, hideUntil - now)
        return () => {
          clearTimeout(id)
        }
      } else {
        setHideUntil(null)
      }
    }
  }, [hideUntil])

  useEffect(() => {
    if (delayedActiveTabIndex !== activeTabIndex) {
      setHideUntil(Date.now() + HIDE_PROMO_BOX_AFTER_TAB_CHANGE_FOR_MS)
      setDelayedActiveTabIndex(activeTabIndex)
    }
  }, [activeTabIndex, delayedActiveTabIndex])

  let promoHidden = hideUntil != null || overlayWithPromoBox(delayedActiveTabIndex) || floatPromoBox == null

  return (
    <>
      <Wrapper ref={resizeRef}>
        <HeaderWrap>
          {!hideHeader && <Header searchTypes={navsInfo?.[currentNavIndex]?.search_types ?? []} />}

          <TabsTitleWrapper
            align={TITLE_ALIGN_MAP[titleAlign]}
            options={tabsInfo?.map((tabInfo: any, tabIndex: number) => ({ id: tabIndex, name: tabInfo?.name }))}
            value={activeTabIndex}
            onChange={(id: number) => handleTabSwitch(id)}
          />
        </HeaderWrap>
        <TabsContentWrapper>
          <Swiper
            key={`nav-${currentNavIndex}`}
            ref={tabContentRef}
            className="navContentWrapper"
            spaceBetween={10}
            thumbs={{ swiper: thumbsSwiper }}
            modules={[Thumbs]}
            onSlideChange={onSlideChange}
            cssMode
          >
            {tabsInfo?.map((tabInfo: any, tabIndex: number) => {
              /** 頻道裡的所有區域資料 及 該頻道是否開啟動態排序 - 頻道設為動態排序時, 以隨機方式顯示所擁有的區域 */
              const isAreaRandom = tabInfo.is_random === TRUE
              const areasInfo = isAreaRandom ? getShuffledArea(tabInfo).sections : tabInfo?.sections

              return (
                <SwiperSlide key={`Slide_${currentNavIndex}_${tabIndex}`} virtualIndex={tabIndex}>
                  {({ isActive }) => (
                    /* WORKAROUND: swiper js 會監聽 img onload 是件並且 reset 進度導致 lazy load 載入的圖片 onload 時顯示異常 */
                    <CmsTab
                      tabInfo={tabInfo}
                      areasInfo={areasInfo}
                      isActive={isActive}
                      onRefresh={() => onRefresh(tabIndex)}
                      contentKey={`${getKeySuffix(currentNavIndex, tabIndex)}`}
                      onShortcutMove={(box) => onPositionChange(tabIndex, box)}
                      displayAreaBox={Math.abs(tabIndex - activeTabIndex) < 1}
                    />
                  )}
                </SwiperSlide>
              )
            })}
          </Swiper>
          {currentNavIndex === 0 && <FloatPromotion ref={onFloatPromoRefed} hidden={promoHidden} />}
        </TabsContentWrapper>
      </Wrapper>
    </>
  )
}
